import React, {
  useState,
  useRef,
  useEffect,
  Fragment,
  useContext,
} from 'react';
import { useParams, useNavigate } from '../../../navigation';

import { TTMenu } from '../../../interfaces/menus';
import { menuApi } from '../../../api/menu';
import { Spin } from 'antd';

import MenuCard from './components/MenuCard';
import store from '../../../store';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store';
import {
  getRestaurant,
  setMenu,
  setMenus,
} from '../../../features/restaurant/restaurant';
import { LoadingIcon } from '../../components/Spinner';
import CartBanner from '../../Cart/components/CartBanner';
import OrderBanner from '../../Order/components/OrderBanner';
import { isMenuAvailable } from '../../utils';
import { getAnalytics, logEvent } from 'firebase/analytics';
import ServiceMessageCard from './components/ServiceMessage';
import RestaurantNameCard from './components/RestaurantNameCard';
import { WalkthroughCTACard } from '../../WalkthroughCTACard';
import { WalkthroughPopUp } from '../../WalkthroughPopUp';
import { RootState } from '../../../rootReducer';
import { TTOrderV2 } from '../../../interfaces/order';
import { PopUpServiceMessage } from '../../PopUpServiceMessage';
import TableTabBanner from '../../components/TableTabBanner';
import { TTRestaurantProfile } from '../../../interfaces/restaurant';
import { CategorizedMenuView } from './components/CategorizedView';
import { FeaturedProducts } from './components/FeaturedProducts';
import { affiliateProductsApi } from '../../../api/affiliate_products';
import { AffiliateProduct } from '../../../interfaces/affiliate_products';
import AffiliateProductCard from './components/AffiliateProductCard';
import { applyTheme, applyWithThemeObject } from '../../../themes/utils';
import { RGBAToHexA } from '../../../utils';
import { TTUserProfile } from '../../../interfaces/user';
import { SavedProductsAccessPoints } from '../../SavedProducts/GuestAccessPoint';
import TrackingContext from '../../Providers/TrackingContext';
import { CheckoutContext } from '../../Providers/CheckoutContext';
import { Dialog, Transition } from '@headlessui/react';
import { PickupSchedulingSlot } from '../../Cart/components/PickupSchedulingSlots';
import { ScheduledOrderTime } from '../../Cart/components/ScheduledOrderTime';
import { menuUrl } from '../../../env';
import axios from 'axios';
import HospitalityLanding from './HospitalityLanding';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Define the gtag function
window.dataLayer = window.dataLayer || [];
window.gtag = function () {
  window.dataLayer.push(arguments);
};

const MenusList: React.FC = () => {
  // @ts-ignore
  const { m_id, r_id } = useParams();
  const menus = useSelector<RootState, TTMenu[]>(
    state => state.restaurant.current_menus,
  );
  const currentCheckout = useContext(CheckoutContext);

  const { trackEvent, trackPageView, trackFBEvent, trackGAEvent } = useContext(
    TrackingContext,
  );

  const [showMenu, setShowMenu] = useState(false);
  const qr_code_menu_ids = useSelector<RootState, { [key: string]: number[] }>(
    state => state.cart.qr_code_menu_ids,
  );
  const qr = useSelector<RootState, string>(state => state.cart.qr_code);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [loading, setLoading] = useState(false);
  const order = useSelector<RootState, TTOrderV2>(state => state.order.order);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [walkthroughShown, setWalkthroughShown] = useState(
    localStorage.getItem('_tt_walkthrough') ?? 'false',
  );
  const [serviceMessageShown, setserviceMessageShown] = useState(
    localStorage.getItem(`__${r_id}_service_message_shown`) ?? 'false',
  );

  const profile = useSelector<RootState, TTUserProfile | null>(
    state => state.auth.userProfile,
  );

  const [hybridClaimed, setHybridClaimed] = useState(
    localStorage.getItem(`__${r_id}_hybrid_claimed`) ?? 'false',
  );

  const [affiliateProducts, setAffiliateProducts] = useState<
    AffiliateProduct[]
  >([]);

  const categorizedView = useSelector<RootState, boolean>(
    state => state.restaurant.categorized_view,
  );

  const [showScheduling, setShowScheduling] = useState(false);

  const menus_only = useSelector<RootState, boolean>(
    state => state.restaurant.menus_only,
  );

  const restaurantProfile = useSelector<RootState, TTRestaurantProfile | null>(
    state => state.restaurant.profile,
  );

  const orderType = useSelector<RootState, string>(
    state => state.cart.order_type,
  );

  useEffect(() => {
    if (restaurantProfile !== null) {
      try {
        if (
          restaurantProfile.custom_theme?.light &&
          restaurantProfile.custom_theme?.dark
        ) {
          localStorage.setItem(
            `__${restaurantProfile?.restaurant_id}__theme`,
            JSON.stringify(restaurantProfile.custom_theme),
          );
          applyWithThemeObject(
            restaurantProfile.custom_theme?.default_mode === 'dark'
              ? restaurantProfile.custom_theme?.dark
              : restaurantProfile.custom_theme?.light,
          );
          const element = document.querySelector('#app-theme-color');
          element.setAttribute(
            'content',
            restaurantProfile?.custom_theme?.default_mode === 'dark'
              ? RGBAToHexA(
                  `${restaurantProfile?.custom_theme?.dark['background']}`,
                )
              : RGBAToHexA(
                  `${restaurantProfile?.custom_theme?.dark['background']}`,
                ),
          );
        }
      } catch (e) {
        console.log(e);
      }

      trackPageView({
        location: window.location,
        title: `${restaurantProfile?.name} - Landing Page`,
      });

      if (restaurantProfile.using_hybrid && hybridClaimed === 'false') {
        navigate(`/feed/restaurants/${r_id}/hybrid/claim-form`);
      }
    }
  }, [restaurantProfile]);

  useEffect(() => {
    let isMounted = true;
    async function fetchMenus() {
      try {
        // @ts-ignore
        const response = await menuApi.getMenusByRestaurant(r_id);
        const affiliateProductsResponse = await affiliateProductsApi.getMerchantProducts(
          parseInt(r_id),
        );
        const newMenus = response.data;
        if (isMounted) {
          dispatch(setMenus(newMenus.sort((a, b) => a.rank - b.rank)));
          setAffiliateProducts(affiliateProductsResponse?.data);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    // Route r_id is a string (useParams); restaurant_id in redux is a number -- guard the
    // comparison with parseInt. A mismatch here means the guest client-side-navigated into a
    // different restaurant's (linked) catalog without this component remounting, so the
    // previously loaded restaurant/theme is stale and needs refetching too.
    const restaurantIdMismatch =
      store.getState().restaurant.restaurant_id !== null &&
      store.getState().restaurant.restaurant_id !== parseInt(r_id);

    if (
      menus == null ||
      store.getState().restaurant.restaurant_id === null ||
      restaurantIdMismatch
    ) {
      if (isMounted) {
        setLoading(true);
      }
      if (
        store.getState().restaurant.restaurant_id === null ||
        restaurantIdMismatch
      ) {
        console.log('restaurant id is null or does not match route r_id');
        dispatch(
          // @ts-ignore
          getRestaurant(r_id, () => {
            fetchMenus();
          }),
        );
      } else {
        fetchMenus();
      }
    } else {
      setLoading(true);
      fetchMenus();
    }
    return () => {
      isMounted = false;
    };
  }, [r_id]);

  useEffect(() => {
    window.scrollTo(0, 0);
    logEvent(getAnalytics(), 'menu_list_view');
    logEvent(getAnalytics(), 'merchant_landing_page_view', {
      merchant_id: r_id,
    });
    // ZendeskAPI('webWidget', 'show');
  }, []);

  useEffect(() => {
    if (
      currentCheckout &&
      currentCheckout.enabled &&
      currentCheckout.scheduled_ordering_enabled &&
      currentCheckout.show_scheduling_at_beginning &&
      (store.getState().cart.order_type === 'pick_up' ||
        store.getState().cart.order_type === 'take_out') &&
      (!store.getState().cart.pickup_details ||
        !store.getState().cart.pickup_details[r_id]?.scheduled_for)
    ) {
      console.log(currentCheckout);
      setShowScheduling(true);
    }
  }, [currentCheckout]);

  // Render hospitality landing page for hotel and STS QR codes
  // if (orderType === 'hotel' || orderType === 'sts') {
  //   return (
  //     <HospitalityLanding
  //       propertyType={orderType === 'hotel' ? 'hotel' : 'sts'}
  //     />
  //   );
  // }

  return (
    <div className="min-h-screen -my-8 py-8 bg-background md:px-8 lg:px-16 xl:px-32">
      <Spin spinning={loading} indicator={LoadingIcon}>
        <div className={' bg-background mb-10'}>
          {store.getState().restaurant.profile !== null && (
            <RestaurantNameCard
              restaurant_name={store.getState().restaurant.profile.name}
              profile_image_url={
                store.getState().restaurant?.profile?.main_profile_image
              }
              bio={store.getState().restaurant?.profile?.bio}
            />
          )}

          {!loading &&
            menus != null &&
            store.getState().restaurant.profile.service_message && (
              <ServiceMessageCard
                message={store.getState().restaurant.profile.service_message}
                restaurant_name={store.getState().restaurant.profile.name}
              />
            )}
          {store.getState().cart.fulfillment_details &&
          (store.getState().cart.order_type === 'pick_up' ||
            store.getState().cart.order_type === 'take_out') &&
          store.getState().cart.pickup_details &&
          store.getState().cart.pickup_details[r_id] &&
          store.getState().cart.pickup_details[r_id].scheduled_for ? (
            <>
              {
                <ScheduledOrderTime
                  scheduled_for={
                    store.getState().cart.pickup_details[r_id].scheduled_for
                  }
                  onClick={() => {
                    setShowScheduling(true);
                  }}
                />
              }
            </>
          ) : (
            <></>
          )}
          {!menus_only && <FeaturedProducts merchant_id={parseInt(r_id)} />}
          {menus != null && !showMenu && !categorizedView && (
            <>
              <div className="grid grid-cols-2 mt-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 items-center rounded-lg gap-6 scrolling-touch p-4">
                {menus.map((menu, i) => {
                  if (
                    qr !== null
                      ? qr_code_menu_ids[qr]?.length === 0 ||
                        qr_code_menu_ids[qr]?.includes(menu?.id)
                      : menu?.offline_viewing
                      ? true
                      : isMenuAvailable(menu?.hours)
                  ) {
                    return (
                      <MenuCard
                        menu={menu}
                        key={i}
                        onClick={async () => {
                          logEvent(getAnalytics(), 'menu_clicked', {
                            menu_id: menu.id,
                            menu_name: menu.name,
                            rank: menu.rank,
                          });
                          trackFBEvent(
                            'CatalogClick',
                            {
                              content_ids: [`ctlg_${menu.id}`],
                              content_type: 'product_group',
                              content_name: [menu.name],
                              content_category: menu.business_type,
                            },
                            true,
                            [menu.restaurant_id],
                          );
                          trackGAEvent(
                            'select_content',
                            {
                              content_type: 'catalog',
                              content_id: `ctlg_${menu.id}`,
                            },
                            [menu.restaurant_id],
                          );
                          if (menu.affiliate_link_mode && menu.affiliate_link) {
                            const trackingUrl = `${menuUrl}/api/v1/affiliate-products/click?is_catalog=true&catalog_id=${
                              menu?.id
                            }&merchant_id=${
                              menu.restaurant_id
                            }&utm_campaign=${'affiliate_marketing'}&utm_medium=${'affiliate_product_card'}&utm_source=${'TT_GUEST_APP'}`;

                            // Using window.location.href to follow the redirect naturally
                            window.open(trackingUrl, '_blank');
                          } else {
                            dispatch(setMenu(menu));
                            navigate(
                              `/feed/restaurants/${r_id}/menus/${menu.id}/`,
                            );
                          }
                        }}
                      />
                    );
                  }
                })}
                {!menus_only &&
                  affiliateProducts?.map(p => {
                    return (
                      <AffiliateProductCard
                        product={p}
                        display_partner_id={parseInt(r_id)}
                        key={`product_${p?.unique_id}`}
                      />
                    );
                  })}
              </div>
            </>
          )}
          {menus !== null && !showMenu && categorizedView && (
            <CategorizedMenuView />
          )}
          {profile !== null && <SavedProductsAccessPoints />}
          {!loading &&
            menus !== null &&
            store.getState().restaurant.profile.is_payment_live &&
            (store.getState().restaurant.profile?.config
              ?.hide_order_instructions ? (
              <></>
            ) : (
              <WalkthroughCTACard
                mb={'mb-10'}
                onClick={() => {
                  setShowWalkthrough(true);
                  localStorage.setItem('_tt_walkthrough', 'true');
                }}
              />
            ))}
          <div className="w-full">
            <TableTabBanner />
          </div>
          {!loading && order === null && <CartBanner />}
          {!loading &&
            order !== null &&
            store.getState().cart.cart_items?.length !== 0 && (
              <div className="relative w-full bottom-16 fixed ">
                <div
                  className="sticky bottom-0"
                  onClick={() => {
                    logEvent(getAnalytics(), 'view_cart_clicked');
                    navigate(`/feed/restaurants/${r_id}/plate`);
                  }}
                >
                  <div className="flex flex-row bg-gray-900 p-4 rounded-t-md items-center justify-center shadow-sm">
                    <div className="flex flex-col justify-center leading-normal">
                      <div className="flex items-center">
                        <div className="text-white font-bold text-lg text-center mx-auto">
                          {'Continue ordering more'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          {!loading && order !== null && (
            <div className="">
              <OrderBanner />
            </div>
          )}
          {!loading &&
            !eval(serviceMessageShown) &&
            store.getState().restaurant.profile?.service_message_config
              ?.as_pop_up && (
              <PopUpServiceMessage
                // @ts-ignore
                restaurant_id={r_id}
                restaurant_name={store.getState().restaurant.profile.name}
                message={store.getState().restaurant.profile.service_message}
                onCancel={() => {
                  localStorage.setItem(
                    `__${r_id}_service_message_shown`,
                    'true',
                  );
                  setserviceMessageShown('true');
                }}
              />
            )}
          {!loading && showWalkthrough && (
            <WalkthroughPopUp
              onCancel={() => {
                localStorage.setItem('_tt_walkthrough', 'true');
                setWalkthroughShown('true');
                setShowWalkthrough(false);
              }}
            />
          )}
        </div>
        {showScheduling ? (
          <>
            <Transition.Root show={showScheduling} as={Fragment} appear>
              <Dialog as="div" className="relative z-10" onClose={() => {}}>
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  afterLeave={() => {
                    setShowScheduling(false);
                  }}
                >
                  <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                  <div className="flex min-h-screen items-end justify-center  text-center sm:items-center sm:p-0">
                    <Transition.Child
                      as={Fragment}
                      enter="ease-out duration-300"
                      enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                      enterTo="opacity-100 translate-y-0 sm:scale-100"
                      leave="ease-in duration-200"
                      leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                      leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                      <Dialog.Panel className="relative transform min-h-screen rounded-lg bg-surface px-2 pt-2 pb-2 text-left shadow-xl transition-all  w-full ">
                        <div className="p-4 md:p-8 lg:px-16 relative">
                          <h3 className="text-on-surface text-lg font-semibold">
                            Select Date & Time for Your Order
                          </h3>
                          <div className="mt-4">
                            <PickupSchedulingSlot
                              onSelect={() => {
                                setShowScheduling(false);
                              }}
                              onCancel={() => {
                                setShowScheduling(false);
                              }}
                            />
                          </div>
                        </div>
                      </Dialog.Panel>
                    </Transition.Child>
                  </div>
                </div>
              </Dialog>
            </Transition.Root>
          </>
        ) : (
          <></>
        )}
      </Spin>
    </div>
  );
};

export default MenusList;
